import { ProductsRoutePending } from "@/components/catalogue/CatalogueRouteSkeletons";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useT } from "@/i18n/LanguageProvider";
import { hreflangLinks, ogLocaleMeta, SITE_URL } from "@/lib/seo";
import { jsonLdScript } from "@/lib/json-ld";
import { findBrandCategory, BRAND_CATEGORIES, type BrandCat } from "@/data/brand-categories";
import { MODEL_LANDINGS } from "@/data/model-landing";
import { listProducts, countBrandCategoryProducts } from "@/lib/catalog.functions";
import { BrandLogo } from "@/components/brand/BrandLogo";
import { BrandShoppingNav } from "@/components/brand/BrandShoppingNav";
import { BrandModelLinks } from "@/components/brand/BrandModelLinks";
import { findBrandSeed } from "@/lib/brand-seo";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ProductCard } from "@/components/ProductCard";
import { CommercialFAQ } from "@/components/CommercialFAQ";
import { PopularGuidesStrip } from "@/components/PopularGuidesStrip";
import { BRAND_CATEGORY_FAQS, faqPageLd } from "@/lib/commercial-faqs";
import { guidesForCategory } from "@/lib/guides-for-category";
import heroPartsImg from "@/assets/hero-parts.jpg";
import { ChevronRight, ShieldCheck, Wallet, RefreshCw } from "lucide-react";

export const Route = createFileRoute("/brand/$slug/$category")({
  pendingComponent: ProductsRoutePending,
  loader: async ({ params, context }) => {
    const hit = findBrandCategory(params.slug, params.category);
    if (!hit) throw notFound();
    const [products, total] = await Promise.all([
      context.queryClient.fetchQuery({
        queryKey: ["brand-category", params.slug, params.category],
        queryFn: () =>
          listProducts({
            data: {
              brand: hit.brand.brand_name,
              category: hit.category.slug,
              limit: 24,
              sort: "popular",
            },
          }),
      }),
      context.queryClient.fetchQuery({
        queryKey: ["brand-category-count", params.slug, params.category],
        queryFn: () =>
          countBrandCategoryProducts({
            data: { brand: hit.brand.brand_name, category: hit.category.slug },
          }),
      }),
    ]);
    return {
      brand: hit.brand,
      category: hit.category,
      products: products ?? [],
      total: total ?? 0,
    };
  },

  head: ({ loaderData, params }) => {
    if (!loaderData) {
      return {
        meta: [{ title: "Not found — Jantro Bazar" }, { name: "robots", content: "noindex" }],
      };
    }
    const { brand, category, products } = loaderData;
    const url = `${SITE_URL}/brand/${params.slug}/${params.category}`;
    const title = `${brand.brand_name} ${category.label_en} in Bangladesh — Genuine & Aftermarket | Jantro Bazar`;
    const desc = `Shop ${brand.brand_name} ${category.label_en.toLowerCase()} in Bangladesh — genuine OEM and aftermarket options from verified sellers. Fit-or-refund guarantee, cash on delivery nationwide.`;
    const ogImage = `${SITE_URL}${heroPartsImg}`;
    const breadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "Brands", item: `${SITE_URL}/brands` },
        {
          "@type": "ListItem",
          position: 3,
          name: brand.brand_name,
          item: `${SITE_URL}/brand/${brand.brand_slug}`,
        },
        { "@type": "ListItem", position: 4, name: category.label_en, item: url },
      ],
    };
    const collectionLd = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: `${brand.brand_name} ${category.label_en}`,
      description: desc,
      url,
      inLanguage: ["en", "bn"],
      isPartOf: { "@type": "WebSite", name: "Jantro Bazar", url: `${SITE_URL}/` },
      about: { "@id": `${SITE_URL}/brand/${brand.brand_slug}#brand` },
    };
    const itemListLd = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: `${brand.brand_name} ${category.label_en} — top listings`,
      numberOfItems: products.length,
      itemListElement: products.slice(0, 20).map((p: any, i: number) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `${SITE_URL}/products/${p.slug}`,
        name: p.title_en,
      })),
    };
    const faqLd = faqPageLd(
      BRAND_CATEGORY_FAQS(
        brand.brand_name,
        brand.brand_name_bn,
        category.label_en,
        category.label_bn,
      ),
    );
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "website" },
        { property: "og:site_name", content: "Jantro Bazar" },
        { property: "og:url", content: url },
        { property: "og:image", content: ogImage },
        {
          property: "og:image:alt",
          content: `${brand.brand_name} ${category.label_en} — Jantro Bazar`,
        },
        ...ogLocaleMeta(),
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
        { name: "twitter:image", content: ogImage },
      ],
      links: [{ rel: "canonical", href: url }, ...hreflangLinks(url)],
      scripts: [
        jsonLdScript(breadcrumb),
        jsonLdScript(collectionLd),
        jsonLdScript(itemListLd),
        jsonLdScript(faqLd),
      ],
    };
  },
  errorComponent: () => (
    <div className="p-16 text-center text-muted-foreground">Failed to load page.</div>
  ),
  notFoundComponent: () => (
    <div className="p-16 text-center text-muted-foreground">Page not found.</div>
  ),
  component: BrandCategoryPage,
});

function BrandCategoryPage() {
  const { brand, category, products, total } = Route.useLoaderData();
  const { lang } = useT();
  const seed = findBrandSeed(brand.brand_slug);
  const fetchMore = useServerFn(listProducts);
  const [extra, setExtra] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [requestError, setRequestError] = useState(false);
  const shown = [...products, ...extra];
  const hasMore = shown.length > 0 && shown.length < total;
  async function loadMore() {
    if (loading) return;
    setLoading(true);
    setRequestError(false);
    try {
      const rows: any = await fetchMore({
        data: {
          brand: brand.brand_name,
          category: category.slug,
          limit: 24,
          offset: shown.length,
          sort: "popular",
        },
      });
      setExtra((e) => [...e, ...((rows ?? []) as any[])]);
    } catch {
      setRequestError(true);
    } finally {
      setLoading(false);
    }
  }
  const en = lang === "en";
  const allBrandModels = MODEL_LANDINGS.filter((m) => m.brand_slug === brand.brand_slug);
  const filteredModels = allBrandModels.filter((m) =>
    m.common_categories.some((cc) => cc.slug === category.slug),
  );
  const models = filteredModels.length > 0 ? filteredModels : allBrandModels;
  const siblingCats = brand.categories.filter((c: BrandCat) => c.slug !== category.slug);

  return (
    <main className="flex-1">
      {/* Hero */}
      <section className="bg-background text-foreground border-b border-border py-5 md:py-7">
        <div className="container-app max-w-5xl">
          <nav aria-label="Breadcrumb" className="text-xs opacity-80 mb-4">
            <ol className="flex flex-wrap items-center gap-1.5">
              <li>
                <Link to="/" className="hover:underline">
                  {en ? "Home" : "হোম"}
                </Link>
              </li>
              <li aria-hidden>
                <ChevronRight className="h-3 w-3 inline opacity-60" />
              </li>
              <li>
                <Link to="/brands" className="hover:underline">
                  {en ? "Brands" : "ব্র্যান্ড"}
                </Link>
              </li>
              <li aria-hidden>
                <ChevronRight className="h-3 w-3 inline opacity-60" />
              </li>
              <li>
                <Link
                  to="/brand/$slug"
                  params={{ slug: brand.brand_slug }}
                  className="hover:underline"
                >
                  {en ? brand.brand_name : brand.brand_name_bn}
                </Link>
              </li>
              <li aria-hidden>
                <ChevronRight className="h-3 w-3 inline opacity-60" />
              </li>
              <li className="font-semibold">{en ? category.label_en : category.label_bn}</li>
            </ol>
          </nav>
          <div
            className={`text-[11px] font-bold opacity-70 mb-2 ${en ? "uppercase tracking-widest" : ""}`}
          >
            {en ? "Brand · Category" : "ব্র্যান্ড · ক্যাটাগরি"}
          </div>
          <div className="flex items-center gap-3">
            {seed?.logoDomain && (
              <BrandLogo
                name={brand.brand_name}
                domain={seed.logoDomain}
                size={48}
                eager
              />
            )}
            <h1 className="min-w-0 font-display text-2xl md:text-3xl font-bold">
              {en
                ? `${brand.brand_name} ${category.label_en.toLowerCase()} `
                : `${brand.brand_name_bn} ${category.label_bn}`}
            </h1>
          </div>
          <p className="mt-2 text-sm text-muted-foreground">{total.toLocaleString(en ? "en" : "bn")} {en ? "parts listed" : "টি পার্টস"}</p>
          <BrandModelLinks brandName={brand.brand_name} domain={seed?.logoDomain ?? ""} category={category.slug} />
        </div>
      </section>

      <section className="container-app pt-4 lg:hidden">
        <BrandShoppingNav
          brandSlug={brand.brand_slug}
          brandName={en ? brand.brand_name : brand.brand_name_bn}
          categories={brand.categories}
          lang={en ? "en" : "bn"}
          activeCategory={category.slug}
        />
      </section>

      {/* Product grid */}
      <section className="container-app py-5 lg:grid lg:grid-cols-[15rem_minmax(0,1fr)] lg:gap-6">
        <BrandShoppingNav
          brandSlug={brand.brand_slug}
          brandName={en ? brand.brand_name : brand.brand_name_bn}
          categories={brand.categories}
          lang={en ? "en" : "bn"}
          activeCategory={category.slug}
          className="hidden lg:block"
        />
        <div className="min-w-0">
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-primary">
              {en
                ? `${brand.brand_name} ${category.label_en.toLowerCase()} — top listings`
                : `${brand.brand_name_bn} ${category.label_bn} — শীর্ষ লিস্টিং`}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {total > 0 && (
                <span className="font-semibold text-foreground tabular-nums">
                  {total} {en ? (total === 1 ? "listing" : "listings") : "টি লিস্টিং"}
                  {" · "}
                </span>
              )}
              {en
                ? "Sorted by real order volume, refreshed daily."
                : "অর্ডার ভলিউম অনুযায়ী সাজানো, প্রতিদিন হালনাগাদ।"}
            </p>
          </div>
          <Link
            to="/products"
            search={{ brand: brand.brand_name, category: category.slug } as any}
            className={`shrink-0 text-xs font-semibold text-primary hover:underline ${en ? "uppercase tracking-widest" : ""}`}
          >
            {en ? "View all →" : "সব দেখুন →"}
          </Link>
        </div>
        {requestError && <div role="alert" className="mb-4 flex flex-wrap items-center gap-3 text-sm text-destructive"><span>{en ? "Could not load more parts." : "আরও পার্টস লোড হয়নি।"}</span><Button variant="outline" onClick={loadMore} disabled={loading}>{en ? "Retry" : "আবার চেষ্টা"}</Button></div>}
        <div aria-busy={loading}>
        {shown.length > 0 ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
              {shown.map((p: any) => (
                <ProductCard key={p.id} p={p} />
              ))}
            </div>
            {hasMore && (
              <div className="mt-6 text-center">
                <Button
                  type="button"
                  onClick={loadMore}
                  disabled={loading}
                  className="inline-flex items-center h-11 rounded-md bg-primary px-6 text-sm font-semibold text-primary-foreground disabled:opacity-60"
                >
                  {loading ? (en ? "Loading…" : "লোড হচ্ছে…") : en ? "Load more" : "আরও দেখুন"}
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            {en
              ? `No ${brand.brand_name} ${category.label_en.toLowerCase()} listed yet. WhatsApp 8801811600591 and we'll source it for you.`
              : `এখনো ${brand.brand_name_bn} ${category.label_bn} লিস্ট করা নেই। 8801811600591-এ হোয়াটসঅ্যাপ করুন, আমরা সোর্স করে দেব।`}
            <div className="mt-4"><Button asChild variant="outline"><a href={`https://wa.me/8801811600591?text=${encodeURIComponent(`${brand.brand_name} ${category.label_en}`)}`} target="_blank" rel="noopener noreferrer">{en ? "Request a quote" : "কোট চান"}</a></Button></div>
          </div>
        )}
        </div>
        </div>
      </section>

      {/* Intro copy */}
      <section className="container-app max-w-3xl py-8">
        <div className="prose prose-neutral max-w-none prose-p:text-muted-foreground prose-p:leading-relaxed prose-strong:text-primary">
          {en ? (
            <>
              <p>
                Every{" "}
                <strong>
                  {brand.brand_name} {category.label_en.toLowerCase()}
                </strong>{" "}
                listing on this page is stocked by a verified seller and comes with a fit-or-refund
                guarantee. Where the seller has provided the OEM part number and cross-references,
                we surface them on the product page so you can double-check against your workshop
                manual before paying.
              </p>
              <p>
                Save your exact model, year, and variant in <strong>My Garage</strong> to hide
                listings that don't fit — or WhatsApp <strong>8801811600591</strong> with the VIN /
                chassis number and the parts advisor will confirm within 30 minutes on business
                hours. Cash on delivery is available in all 64 districts, and bKash / Nagad / card
                are accepted at checkout.
              </p>
            </>
          ) : (
            <>
              <p>
                এই পেজের প্রতিটি{" "}
                <strong>
                  {brand.brand_name_bn} {category.label_bn}
                </strong>{" "}
                লিস্টিং যাচাইকৃত বিক্রেতার কাছ থেকে এবং ফিট-অথবা-রিফান্ড গ্যারান্টির আওতায়।
                বিক্রেতা OEM পার্ট নম্বর ও ক্রস-রেফারেন্স দিলে প্রোডাক্ট পেজে দেখানো হয় — টাকা
                দেওয়ার আগে ওয়ার্কশপ ম্যানুয়ালের সাথে মিলিয়ে নিতে পারবেন।
              </p>
              <p>
                <strong>My Garage</strong>-এ সঠিক মডেল, বছর ও ভ্যারিয়েন্ট সেভ করুন —
                অসামঞ্জস্যপূর্ণ লিস্টিং লুকিয়ে যাবে। অথবা <strong>8801811600591</strong> নম্বরে VIN
                / চেসিস নম্বর হোয়াটসঅ্যাপ করুন — ব্যবসায়িক সময়ে ৩০ মিনিটে পার্টস অ্যাডভাইজর
                নিশ্চিত করবে। সব ৬৪ জেলায় ক্যাশ অন ডেলিভারি; চেকআউটে বিকাশ / নগদ / কার্ড গৃহীত।
              </p>
            </>
          )}
        </div>
      </section>

      {/* Popular models for this brand */}
      {models.length > 0 && (
        <section className="container-app max-w-6xl py-10">
          <div className="mb-6">
            <h2 className="text-2xl md:text-3xl font-bold text-primary">
              {en ? `Popular ${brand.brand_name} models` : `জনপ্রিয় ${brand.brand_name_bn} মডেল`}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {en
                ? "Jump to a model-specific parts page for year and variant fit help."
                : "বছর ও ভ্যারিয়েন্ট ফিটমেন্ট সাহায্যের জন্য মডেল-নির্দিষ্ট পার্টস পেজে যান।"}
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {models.map((m) => (
              <Link
                key={m.slug}
                to="/parts/$makeModel"
                params={{ makeModel: m.slug }}
                search={{ cat: category.slug }}
                className="group rounded-lg border border-border bg-card p-4 hover:border-primary/60 hover:bg-secondary/40 transition-colors"
              >
                <div
                  className={`text-[10px] font-bold text-primary mb-1 ${en ? "uppercase tracking-widest" : ""}`}
                >
                  → {en ? "Model parts" : "মডেল পার্টস"}
                </div>
                <div className="font-bold text-primary leading-snug">
                  {en ? `${m.make} ${m.model}` : `${m.make_bn} ${m.model_bn}`}
                </div>
                <div className="text-[11px] text-muted-foreground mt-1">{m.years_en}</div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Sibling categories on same brand */}
      {siblingCats.length > 0 && (
        <section className="container-app max-w-6xl py-10">
          <div className="mb-4">
            <h2 className="text-xl md:text-2xl font-bold text-primary">
              {en
                ? `Other ${brand.brand_name} categories`
                : `${brand.brand_name_bn}-এর অন্যান্য ক্যাটাগরি`}
            </h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {siblingCats.map((c: BrandCat) => (
              <Link
                key={c.slug}
                to="/brand/$slug/$category"
                params={{ slug: brand.brand_slug, category: c.slug }}
                className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-4 min-h-11 text-xs font-semibold text-primary hover:border-primary/60"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-primary" aria-hidden />
                {en ? c.label_en : c.label_bn}
              </Link>
            ))}
            <Link
              to="/brand/$slug"
              params={{ slug: brand.brand_slug }}
              className="inline-flex items-center gap-1 rounded-full bg-primary text-primary-foreground px-4 min-h-11 text-xs font-semibold"
            >
              {en ? `All ${brand.brand_name} parts` : `সব ${brand.brand_name_bn} পার্টস`}{" "}
              <ChevronRight className="h-3 w-3" />
            </Link>
          </div>
        </section>
      )}

      {/* Popular guides */}
      <section className="container-app max-w-6xl py-10">
        <PopularGuidesStrip
          items={guidesForCategory(category.slug, 6)}
          headingEn={`Before you buy ${brand.brand_name} ${category.label_en.toLowerCase()}`}
          headingBn={`${brand.brand_name_bn} ${category.label_bn} কেনার আগে`}
        />
      </section>

      {/* FAQ */}
      <section className="container-app max-w-5xl py-10 pb-16">
        <CommercialFAQ
          items={BRAND_CATEGORY_FAQS(
            brand.brand_name,
            brand.brand_name_bn,
            category.label_en,
            category.label_bn,
          )}
          headingEn={`${brand.brand_name} ${category.label_en} — frequently asked`}
          headingBn={`${brand.brand_name_bn} ${category.label_bn} — প্রায়শই জিজ্ঞাসিত`}
        />
      </section>
    </main>
  );
}

function HeroChip({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-md border border-primary-foreground/20 bg-primary-foreground/10 px-3 h-7 text-[11px] font-semibold ${/[\u0980-\u09FF]/.test(label) ? "" : "uppercase"}`}>
      {icon}
      {label}
    </span>
  );
}

// Referenced only to keep BRAND_CATEGORIES import tree-shakeable free of
// dead-code warnings when eslint runs on this route in isolation.
export const _brandCount = BRAND_CATEGORIES.length;
